/**
 * Auth Callback Handler
 *
 * Handles PKCE code exchange and recovery/magic link flows.
 * This route is the target for all Supabase auth redirects.
 *
 * Flow:
 * 1. Check for auth params (code, access_token, recovery)
 * 2. Exchange code for session (if PKCE)
 * 3. Clean URL params
 * 4. Route to appropriate destination based on auth state
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { cleanAuthParamsFromUrl, hasRecoveryParams } from '@/core/auth/redirect-utils';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

type CallbackState = 'processing' | 'success' | 'error';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<CallbackState>('processing');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        const errorParam = url.searchParams.get('error');
        const errorDescription = url.searchParams.get('error_description');

        // Handle error from Supabase
        if (errorParam) {
          console.error('[AuthCallback] Error from Supabase:', errorParam, errorDescription);
          setError(errorDescription || errorParam);
          setState('error');
          return;
        }

        // Check for recovery flow (password reset)
        if (hasRecoveryParams()) {
          console.log('[AuthCallback] Recovery flow detected, redirecting to reset-password');
          // For recovery, we need to let the session be established first
          const { data, error: sessionError } = await supabase.auth.getSession();

          if (sessionError) {
            console.error('[AuthCallback] Session error during recovery:', sessionError);
            // Don't fail - the reset-password page will handle this
          }

          cleanAuthParamsFromUrl();
          navigate('/auth/reset-password', { replace: true });
          return;
        }

        // Handle PKCE code exchange
        if (code) {
          console.log('[AuthCallback] Exchanging code for session');
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            console.error('[AuthCallback] Code exchange failed:', exchangeError);
            setError(exchangeError.message);
            setState('error');
            return;
          }

          if (data.session) {
            console.log('[AuthCallback] Session established successfully');
            cleanAuthParamsFromUrl();
            setState('success');

            // Check for return path
            const returnTo = searchParams.get('returnTo');
            if (returnTo && returnTo.startsWith('/')) {
              navigate(returnTo, { replace: true });
            } else {
              navigate('/app', { replace: true });
            }
            return;
          }
        }

        // No auth params - check if already authenticated
        const { data: { session } } = await supabase.auth.getSession();

        if (session) {
          console.log('[AuthCallback] Already authenticated, redirecting to app');
          cleanAuthParamsFromUrl();
          navigate('/app', { replace: true });
        } else {
          console.log('[AuthCallback] No session and no auth params, redirecting to login');
          navigate('/login', { replace: true });
        }
      } catch (err) {
        console.error('[AuthCallback] Unexpected error:', err);
        setError('An unexpected error occurred during authentication.');
        setState('error');
      }
    };

    handleCallback();
  }, [navigate, searchParams]);

  if (state === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="glass-strong rounded-2xl p-8">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Authentication Failed</h1>
            <p className="text-muted-foreground mb-6">
              {error || 'An error occurred during authentication.'}
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/login', { replace: true })}
                className="w-full"
              >
                Return to Login
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // Clear any stale session data and retry
                  supabase.auth.signOut().then(() => {
                    localStorage.clear();
                    window.location.href = '/login';
                  });
                }}
                className="w-full"
              >
                Clear Session & Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Processing state
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-muted-foreground">Completing authentication...</p>
      </div>
    </div>
  );
}
